<template>
  <div class="itin-flight itin-extras">
    <div class="itin-subheader">
      <h4>
        <font-awesome-icon icon="suitcase"/>
        {{ $t('Extras') }}
      </h4>
    </div>
    <div class="extras-page">
      <div v-for="pax in passengers" :key="pax.PassengerID" class="extras-pax">
        <h3>
          <font-awesome-icon icon="user"/>
          {{paxName(pax)}}
        </h3>
        <div v-for="segment in pax.Segments" :key="segment.SegmentID" class="extras-segment">
          <div class="extras-segment-title">
            <font-awesome-icon icon="plane"/>
            {{segment.DepartureStation}} → {{segment.ArrivalStation}}
            <span v-if="segment.DepartureDate"> · {{formatDate(segment.DepartureDate)}}</span>
          </div>
          <div class="extra-card-list" v-if="extrasFor(segment).length">
            <div class="extra-card extra-card-wide extra-card-readonly" v-for="extra in extrasFor(segment)" :key="extra.SSRCode">
              <div class="extra-card-icon extra-card-icon-lg">
                <font-awesome-icon :icon="extra.Icon || 'suitcase'"/>
              </div>
              <div class="extra-card-body">
                <div class="extra-card-name">{{extraTitle(extra)}}</div>
                <div class="extra-card-dims" v-if="extraWeightLabel(extra) || extraDimensionsLabel(extra)">
                  <span v-if="extraWeightLabel(extra)">{{extraWeightLabel(extra)}}</span>
                  <span v-if="extraWeightLabel(extra) && extraDimensionsLabel(extra)"> · </span>
                  <span v-if="extraDimensionsLabel(extra)">{{extraDimensionsLabel(extra)}}</span>
                </div>
                <div class="extra-card-sub" v-if="extraRulesLabel(extra)">{{extraRulesLabel(extra)}}</div>
                <div class="extra-card-price">{{formatPrice(extra)}}</div>
              </div>
            </div>
          </div>
          <p v-else class="extras-intro mb-0">{{ $t('Sin extras en este tramo.') }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { getAvailableExtras } from '@/api/api';
  import { mapState } from 'vuex';
  import moment from 'moment';
  import { formatMoney } from '@/utils/utils';
  import { normalizeText } from '@/utils/tools';

  export default {
    name: 'extras-list',
    data: function () {
      return {
        catalog: [],
      }
    },
    computed: {
      ...mapState(['booking', 'lastName', 'dni']),
      passengers: function () {
        if (!this.booking || !this.booking.Passengers) return [];
        return this.booking.Passengers.filter((pax) => {
          if (pax.Infant || pax.PaxType === 'INF') return false;
          if (this.lastName && this.tlc(normalizeText(this.lastName)) != this.tlc(normalizeText(pax.LastName))) return false;
          if (this.dni && this.tlc(this.dni) != this.tlc(pax.DocNumber)) return false;
          return true;
        });
      }
    },
    mounted: function () {
      let bookingId = this.booking && this.booking.BookingID;
      getAvailableExtras((result) => {
        this.catalog = Array.isArray(result) ? result : [];
      }, bookingId);
    },
    methods: {
      tlc: function (t) {
        return t.replace(/ /g, '').toLowerCase();
      },
      paxName: function (pax) {
        return [pax.FirstName, pax.MiddleName, pax.LastName].filter(Boolean).join(' ');
      },
      formatDate: function (d) {
        return moment(d).locale('es').format('DD MMM YYYY');
      },
      catalogItem: function (code) {
        return _.find(this.catalog, (item) => item.SSRCode === code || item.FeeCode === code);
      },
      formatCatalogNumber: function (value) {
        if (value === null || value === undefined || value === '') return value;
        let num = parseFloat(value);
        if (isNaN(num)) return value;
        return num === parseInt(num, 10) ? String(parseInt(num, 10)) : String(num);
      },
      extraTitle: function (extra) {
        if (!extra) return '';
        if (extra.I18nKey) {
          let keyed = this.$t(extra.I18nKey + '.title');
          if (keyed && keyed !== extra.I18nKey + '.title') return keyed;
        }
        return this.$t(extra.Name || extra.SSRCode);
      },
      extraWeightLabel: function (extra) {
        if (!extra) return null;
        if (extra.I18nKey && extra.MaxWeight != null) {
          let keyed = this.$t(extra.I18nKey + '.short_desc', {
            weight: this.formatCatalogNumber(extra.MaxWeight),
            unit: (extra.WeightUnit || 'KG').toLowerCase(),
          });
          if (keyed && keyed !== extra.I18nKey + '.short_desc') return keyed;
        }
        return null;
      },
      extraDimensionsLabel: function (extra) {
        if (!extra) return null;
        if (extra.I18nKey && extra.MaxDimLength != null && extra.MaxDimWidth != null && extra.MaxDimHeight != null) {
          let keyed = this.$t(extra.I18nKey + '.dimensions', {
            height: this.formatCatalogNumber(extra.MaxDimHeight),
            width: this.formatCatalogNumber(extra.MaxDimWidth),
            depth: this.formatCatalogNumber(extra.MaxDimLength),
            dim_unit: (extra.DimUnit || 'CM').toLowerCase(),
          });
          if (keyed && keyed !== extra.I18nKey + '.dimensions') return keyed;
        }
        return null;
      },
      extraRulesLabel: function (extra) {
        if (!extra) return null;
        if (extra.I18nKey) {
          let keyed = this.$t(extra.I18nKey + '.rules');
          if (keyed && keyed !== extra.I18nKey + '.rules') return keyed;
        }
        return null;
      },
      isExtraCode: function (code) {
        if (!code) return false;
        if (this.catalogItem(code)) return true;
        return false;
      },
      extrasFor: function (segment) {
        let seen = [];
        let extras = [];
        let push = (code, name, amount, currency) => {
          if (!this.isExtraCode(code) || seen.indexOf(code) > -1) return;
          seen.push(code);
          let catalog = this.catalogItem(code) || {};
          let merged = Object.assign({}, catalog, {
            SSRCode: code,
            Name: catalog.Name || name || code,
            Icon: catalog.Icon || 'suitcase',
            BasePrice: amount != null ? amount : catalog.BasePrice,
            CurrencyCode: currency || catalog.CurrencyCode || 'ARS',
          });
          if (segment && catalog.BySegment && catalog.BySegment[String(segment.SegmentID)]) {
            Object.assign(merged, catalog.BySegment[String(segment.SegmentID)]);
          }
          extras.push(merged);
        };
        for (let fee of segment.Fees || []) {
          let amount = 0;
          for (let charge of fee.Charges || []) {
            if (charge.ChargeType == 3) continue;
            let k = charge.ChargeType == 1 ? -1 : 1;
            amount += parseFloat(charge.ChargeAmount || 0) * k;
          }
          let name = (fee.Fee && (fee.Fee.Name || fee.Fee.Description)) || fee.FeeDetail || fee.FeeCode;
          let currency = fee.Charges && fee.Charges[0] ? fee.Charges[0].CurrencyCode : 'ARS';
          push(fee.SSRCode || fee.FeeCode, name, amount, currency);
        }
        if (segment.Legs && segment.Legs[0]) {
          for (let ssr of segment.Legs[0].SSRs || []) {
            let name = ssr.SSR && ssr.SSR.Name ? ssr.SSR.Name : ssr.SSRCode;
            push(ssr.SSRCode || ssr.FeeCode, name, null, null);
          }
        }
        return extras;
      },
      formatPrice: function (extra) {
        if (extra.BasePrice === null || extra.BasePrice === undefined || extra.BasePrice === '') {
          return '';
        }
        let amount = parseFloat(extra.BasePrice);
        if (isNaN(amount) || amount === 0) return this.$t('Sin cargo');
        return (extra.CurrencyCode || 'ARS') + ' ' + formatMoney(amount);
      }
    }
  }
</script>
